import React, { useState, useEffect } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Business as BusinessIcon, Cloud as CloudIcon } from '@mui/icons-material';
import { useCustomer, customerKey } from '../../contexts/CustomerContext';
import { useAuthContext } from '../../Auth';

const CustomerSelector: React.FC = () => {
  const { tokenData } = useAuthContext();
  const { currentCustomer, availableCustomers, loadCustomers, setCustomer, isLoading } = useCustomer();
  const [localLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    if (currentCustomer) {
      loadCustomers().catch(err => {
        console.error('Failed to load customers:', err);
      });
    }
  }, [currentCustomer]);

  const handleCustomerChange = async (event: any) => {
    const key = event.target.value as string;
    if (!tokenData || !key) return;
    const [tenant, environment] = key.split(':');
    if (!tenant || !environment) return;

    try {
      setLocalLoading(true);
      await setCustomer(tenant, environment, tokenData.email);
    } catch (err: any) {
      console.error('Failed to switch customer:', err);
      setLocalLoading(false);
    }
  };

  if (isLoading || !currentCustomer) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
        <CircularProgress size={20} color="inherit" />
        <Typography variant="body2">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Prominent current-context chip: customer_name + environment.
          Rendered outside the Select so the user always sees the active
          tenant/env pair even when the dropdown is closed. */}
      <Chip
        icon={<BusinessIcon />}
        label={
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {currentCustomer.customer_name}
            </Box>
            <Box component="span" sx={{ opacity: 0.6 }}>/</Box>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                fontFamily: 'monospace',
                fontSize: '0.85em',
              }}
            >
              <CloudIcon sx={{ fontSize: 14 }} />
              {currentCustomer.environment}
            </Box>
          </Box>
        }
        sx={{
          bgcolor: 'rgba(255,255,255,0.15)',
          color: 'white',
          borderRadius: 1.5,
          height: 32,
          '.MuiChip-icon': { color: 'white' },
          fontSize: '0.9rem',
          px: 0.5,
        }}
      />
      <FormControl size="small" sx={{ minWidth: 100 }}>
        <Select
          value={customerKey(currentCustomer)}
          onChange={handleCustomerChange}
          disabled={localLoading || availableCustomers.length === 0}
          renderValue={() => 'Switch'}
          sx={{
            color: 'white',
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'white',
            },
            '.MuiSvgIcon-root': {
              color: 'white',
            },
          }}
        >
          {availableCustomers.map((customer) => (
            <MenuItem key={customerKey(customer)} value={customerKey(customer)}>
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <span>{customer.customer_name}</span>
                <Chip
                  size="small"
                  label={customer.environment}
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default CustomerSelector;
